import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { AppInitialProps } from 'next/app';
import { compose } from 'redux';
import { withAccount } from '~/hooks/with-account';
import { withApolloClient } from '~/graphql/client';
import Typography from '@mui/material/Typography';
import Layout from '~/components/layout';
import { StyledComponentProps } from '~/types/material-ui';
import { useStyles } from './styles';
import { EcosystemItemType } from '~/enums/ecosystem.enum';
import ImageList from '@mui/material/ImageList';
import EcosystemItem from './ecosystem-item';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '~/store/configure-store';
import * as UserStateActions from '~/store/actions/user-state-actions';
import * as Sentry from '@sentry/nextjs';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import clsx from 'clsx';
import { AppRoutesEnum } from '~/enums/route.enum';
import Link from 'next/link';
import Button from '@mui/material/Button';
import { useMediaQuery, useTheme } from '@mui/material';

interface Props extends AppInitialProps, StyledComponentProps<typeof useStyles> {
  category?: EcosystemItemType;
}

const Ecosystem = (props: Props) => {
  const { classes } = useStyles(undefined, { props: { classes: props.classes } });
  const { t } = useTranslation('common');
  const tabSelectList = useMemo(() => {
    return [
      'all',
      ...Object.values(EcosystemItemType).filter((item) => item !== EcosystemItemType.STABLE_COIN),
    ];
  }, []);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const selectedTabIndex = useMemo(
    () => (props.category ? tabSelectList.findIndex((tab) => tab === props.category) : 0),
    [props.category, tabSelectList],
  );
  const chipRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (isSmallScreen && selectedTabIndex !== -1 && chipRefs.current[selectedTabIndex]) {
      chipRefs.current[selectedTabIndex]?.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      });
    }
  }, [selectedTabIndex, isSmallScreen]);

  const dispatch = useDispatch();
  const ecosystemApps = useSelector((state: RootState) => state.userState?.ecosystemApps);

  useEffect(() => {
    const fetchApps = async () => {
      if (!ecosystemApps || ecosystemApps.length === 0) {
        try {
          setLoading(true);
          await dispatch(UserStateActions.getEcosystemAppsAction());
        } catch (error) {
          console.error(error);
          Sentry.captureException(error);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchApps();
  }, [dispatch, ecosystemApps]);

  const ecosystemItems = useMemo(() => {
    const displayableEcosystemApps = ecosystemApps?.filter(
      (item) => !item.types.includes(EcosystemItemType.STABLE_COIN),
    );
    if (selectedTabIndex === 0) {
      return displayableEcosystemApps;
    }

    return displayableEcosystemApps?.filter((item) => {
      return item.types.includes(tabSelectList[selectedTabIndex] as EcosystemItemType);
    });
  }, [selectedTabIndex, ecosystemApps, tabSelectList]);

  if (selectedTabIndex === -1) {
    return (
      <Layout>
        <div className={classes.errorFetchLayout}>
          <div className={classes.errorPaper}>
            <Typography
              variant="h4"
              className={classes.errorTitle}
              data-testid="no-ecosystem-title"
            >
              {t('no_ecosystem')}
            </Typography>
            <Typography
              variant="body2"
              className={classes.errorMessage}
              data-testid="no-ecosystem-message"
            >
              {t('no_ecosystem_on_condition_description')}
            </Typography>
            <div className={classes.errorButtonWrapper}>
              <Link href={AppRoutesEnum.DASHBOARD}>
                <Button
                  className={classes.errorButton}
                  data-testid="back-to-dashboard-button"
                  variant="contained"
                  color="primary"
                >
                  {t('dashboard')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Typography className={classes.pageTitle}>{t('ecosystem_page.title')}</Typography>
      <div className={classes.content}>
        <div className={classes.chipContainer}>
          {tabSelectList.map((tab, index) => (
            <div
              key={index}
              ref={(el) => (chipRefs.current[index] = el)}
              style={{ display: 'inline-block' }}
            >
              <Link
                href={index === 0 ? AppRoutesEnum.ECOSYSTEM : `${AppRoutesEnum.ECOSYSTEM}/${tab}`}
              >
                <Chip
                  className={clsx(classes.chip, {
                    [classes.selectedChip]: selectedTabIndex === index,
                  })}
                  label={t(`ecosystem_page.${tab}`)}
                  variant="outlined"
                  data-testid={`ecosystem-tab-${tab}`}
                />
              </Link>
            </div>
          ))}
        </div>
        {tabSelectList.map((_, index) => (
          <div key={index} hidden={selectedTabIndex !== index}>
            {!loading && ecosystemItems && (
              <ImageList className={classes.imageList}>
                {ecosystemItems.map((item, itemIndex) => (
                  <EcosystemItem
                    data-testid={`ecosystem-item-${item.name}`}
                    key={`${item.name}-${itemIndex}`}
                    ecosystemItem={item}
                  />
                ))}
              </ImageList>
            )}
          </div>
        ))}
        {loading && (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        )}
      </div>
    </Layout>
  );
};

export default compose(withApolloClient, withAccount)(Ecosystem);
